import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { lvProductService } from '../services/lvProductService';
import { queryKeys } from '@/lib/queryKeys';
import { toast } from 'sonner';
import type { LVProduct } from '../types';
import { logger } from '@/lib/logger';

type MutationToastOptions = {
  showSuccessToast?: boolean;
};

export function useLVProducts(floorPlanId: string) {
  const queryClient = useQueryClient();

  // Fetch all LV products for a floor plan
  const {
    data: products,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
    queryFn: () => lvProductService.getByFloorPlan(floorPlanId),
    enabled: !!floorPlanId,
  });

  // Create LV product mutation with optimistic updates
  const createMutation = useMutation({
    mutationFn: ({
      productData,
    }: {
      productData: Parameters<typeof lvProductService.create>[0];
      options?: MutationToastOptions;
    }) => lvProductService.create(productData),
    onMutate: async ({ productData: newProduct }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
      });

      // Snapshot the previous value
      const previousProducts = queryClient.getQueryData<LVProduct[]>([
        ...queryKeys.floorPlan(floorPlanId),
        'lv-products',
      ]);

      // Create temporary product with temp ID
      const tempProduct: LVProduct = {
        ...newProduct,
        id: `temp-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as LVProduct;

      // Optimistically add to cache
      queryClient.setQueryData<LVProduct[]>(
        [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
        (old = []) => [...old, tempProduct],
      );

      logger.log('[Optimistic] Created LV product with temp ID:', tempProduct.id);

      // Return context object with snapshotted value
      return { previousProducts, tempProduct };
    },
    onSuccess: (realProduct, variables, context) => {
      // Replace the temporary product with the real one from server
      queryClient.setQueryData<LVProduct[]>(
        [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
        (old = []) =>
          old.map(product => (product.id === context?.tempProduct.id ? realProduct : product)),
      );

      logger.log('[Optimistic] Replaced temp LV product with real ID:', realProduct.id);
      if (variables.options?.showSuccessToast !== false) {
        toast.success('Product created successfully');
      }
    },
    onError: (error, variables, context) => {
      logger.error('Failed to create product:', error);
      toast.error('Failed to create product');

      // Rollback to previous state on error
      if (context?.previousProducts) {
        queryClient.setQueryData(
          [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
          context.previousProducts,
        );
      } else {
        // Force refetch to ensure cache is in sync
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
        });
      }
    },
    onSettled: (data, error) => {
      // Invalidate on error to ensure cache is in sync
      if (error) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
        });
      }
    },
  });

  // Update LV product mutation with optimistic updates
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Parameters<typeof lvProductService.update>[1];
    }) => lvProductService.update(id, updates),
    onMutate: async ({ id, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
      });

      // Snapshot the previous value
      const previousProducts = queryClient.getQueryData<LVProduct[]>([
        ...queryKeys.floorPlan(floorPlanId),
        'lv-products',
      ]);

      // Optimistically update the product
      queryClient.setQueryData<LVProduct[]>(
        [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
        (old = []) =>
          old.map(product => {
            if (product.id === id) {
              return {
                ...product,
                ...updates,
                updated_at: new Date().toISOString(),
              } as LVProduct;
            }
            return product;
          }),
      );

      logger.log('[Optimistic] Updated LV product:', id);

      // Return context object with snapshotted value
      return { previousProducts };
    },
    onSuccess: () => {
      // Silent success for drag operations
    },
    onError: (error, variables, context) => {
      logger.error('Failed to update product:', error);
      toast.error('Failed to update product');

      // Rollback to previous state on error
      if (context?.previousProducts) {
        queryClient.setQueryData(
          [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
          context.previousProducts,
        );
      }
    },
  });

  // Delete LV product mutation with optimistic update
  const deleteMutation = useMutation({
    mutationFn: ({ id }: { id: string; options?: MutationToastOptions }) =>
      lvProductService.delete(id),
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
      });

      // Snapshot the previous value
      const previousProducts = queryClient.getQueryData<LVProduct[]>([
        ...queryKeys.floorPlan(floorPlanId),
        'lv-products',
      ]);

      // Optimistically remove the product
      queryClient.setQueryData<LVProduct[]>(
        [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
        (old = []) => {
          return old.filter(product => product.id !== id);
        },
      );

      // Return a context object with the snapshotted value
      return { previousProducts };
    },
    onSuccess: (_data, variables) => {
      if (variables.options?.showSuccessToast !== false) {
        toast.success('Product deleted successfully');
      }
    },
    onError: (error, variables, context) => {
      logger.error('Failed to delete product:', error);
      toast.error('Failed to delete product');

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousProducts) {
        queryClient.setQueryData(
          [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
          context.previousProducts,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
      });
    },
  });

  return {
    products,
    isLoading,
    error,
    refetch,
    createProduct: (
      productData: Parameters<typeof lvProductService.create>[0],
      options?: MutationToastOptions,
    ) => createMutation.mutateAsync({ productData, options }),
    updateProduct: updateMutation.mutate,
    updateProductAsync: updateMutation.mutateAsync,
    deleteProduct: (id: string, options?: MutationToastOptions) =>
      deleteMutation.mutateAsync({ id, options }),
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

// Hook to get a single LV product
export function useLVProduct(id?: string) {
  const {
    data: product,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['lv-product', id],
    queryFn: () => (id ? lvProductService.getById(id) : null),
    enabled: !!id,
  });

  return {
    product,
    isLoading,
    error,
  };
}
